/**
 * QR Code Utilities for ThreadHeads Production App
 *
 * QR codes use format: THP://<base64-encoded-json>
 *
 * Payload types (short keys — `v` is the schema version, but classification &
 * id extraction route on KEY PRESENCE, not `v`, so a version bump never breaks
 * scanning):
 * - Garment QR: { v: number, g: string, o?: string, s?: string, m?: string, t?: string }
 * - Bin QR: { v: number, b: string }
 *
 * Legacy payload types (long keys):
 * - Garment QR: { garment_unit_id: string }
 * - Bin QR: { bin_id: string }
 * - Shelf QR: { shelf_id: string }
 */

export interface GarmentQRPayload {
  garment_unit_id: string;
}

export interface BinQRPayload {
  bin_id: string;
}

export interface ShelfQRPayload {
  shelf_id: string;
}

export type QRPayload = GarmentQRPayload | BinQRPayload | ShelfQRPayload;

/**
 * Decode a ThreadHeads Production QR code.
 * Returns null if the QR code is not in the expected format.
 *
 * @param qrCode The scanned QR code string
 * @returns Decoded payload or null if invalid
 */
export function decodeThpQR<T extends QRPayload = QRPayload>(qrCode: string): T | null {
  if (!qrCode || typeof qrCode !== 'string' || !qrCode.startsWith('THP://')) {
    return null;
  }

  try {
    const base64 = qrCode.slice(6); // Remove 'THP://' prefix
    if (!base64) return null;

    const decoded = atob(base64);
    const parsed = JSON.parse(decoded);

    // Validate it's an object
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as T;
  } catch {
    return null;
  }
}

/** ThreadHeads Production QR protocol prefix. */
const QR_PREFIX = 'THP://';

/**
 * Is a raw scanned string a COMPLETE, submittable THP payload?
 *
 * USB keyboard-wedge scanners feed a long `THP://<base64-json>` garment QR
 * (~142 chars) as a rapid keystroke burst terminated by Enter. Under main-thread
 * jank the trailing characters can lag behind the Enter, so reading the input
 * value on Enter yields a TRUNCATED payload — a `THP://` prefix followed by
 * base64 whose JSON never closes. That decodes to nothing → the backend throws
 * "Invalid QR code: failed to parse JSON". This bit single-item packing hardest:
 * those orders have no bin, so the packer MUST scan the long garment QR, whereas
 * multi-item orders scan the short plain-text `THP://BIN:<label>` code that
 * completes long before the race window.
 *
 * QRScannerInput uses this to decide whether a scan is safe to submit or should
 * be held a beat for its tail to land. Returns `true` for everything that is
 * safe to submit as-is:
 * - non-THP values (manual entry / other code types — existing validation handles them),
 * - plain-text THP forms (`THP://BIN:`, `THP://BENCH:`, `THP://SHELF:` — short + robust),
 * - any THP base64 body that decodes to JSON.
 *
 * Returns `false` for a THP base64 body that does not yet decode (a truncated,
 * still-arriving scan) AND for a partial `THP://` prefix (a scan whose Enter beat
 * even the protocol prefix). Everything else — non-THP values, plain-text THP
 * forms, and decodable base64 — is `true`.
 */
export function isThpPayloadComplete(qrCode: string): boolean {
  if (!qrCode) return true; // nothing to submit — caller guards empty separately
  // A value that is still only a partial "THP://" prefix (e.g. "THP", "THP:/") is
  // a scan mid-stream: an Enter arrived before the protocol prefix finished. Hold
  // it so the rest of the scan can append instead of the field being cleared out
  // from under the incoming characters. Any other short string is a manual/other
  // entry — let it through (existing validation handles it).
  if (qrCode.length < QR_PREFIX.length) {
    return !QR_PREFIX.startsWith(qrCode);
  }
  if (!qrCode.startsWith(QR_PREFIX)) return true;
  const body = qrCode.slice(QR_PREFIX.length);
  if (!body) return false; // bare "THP://" — nothing has arrived yet
  // Plain-text forms carry their meaning literally; they are not base64-JSON and
  // are short enough not to be the truncation failure mode. FILM: is the
  // pre-printed DTF film label (BLU-233) — without it a THP://FILM:<uuid> scan
  // falls through to the base64 decode below, fails to parse, and is wrongly
  // held as a truncated scan (never submitting) at the press station. FILMPH:
  // is the film-storage slot token (BLU-685; HOLE: kept for legacy stickers) —
  // note FILMPH: is distinct from FILM: (the char after FILM is P, not ':').
  if (/^(BIN:|BENCH:|SHELF:|HOLE:|FILMPH:|FILM:)/.test(body)) return true;
  try {
    // Mirrors the backend parse step (base64 → JSON). If the tail is missing the
    // JSON will not parse and we report "incomplete".
    JSON.parse(atob(body));
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard for garment QR payloads.
 * Validates both key existence AND value type.
 */
export function isGarmentQR(payload: QRPayload | null): payload is GarmentQRPayload {
  if (payload === null || !('garment_unit_id' in payload)) return false;
  const id = payload.garment_unit_id;
  return typeof id === 'string' && id.length > 0;
}

/**
 * Type guard for bin QR payloads.
 * Validates both key existence AND value type.
 */
export function isBinQR(payload: QRPayload | null): payload is BinQRPayload {
  if (payload === null || !('bin_id' in payload)) return false;
  const id = payload.bin_id;
  return typeof id === 'string' && id.length > 0;
}

/**
 * Type guard for shelf QR payloads.
 * Validates both key existence AND value type.
 */
export function isShelfQR(payload: QRPayload | null): payload is ShelfQRPayload {
  if (payload === null || !('shelf_id' in payload)) return false;
  const id = payload.shelf_id;
  return typeof id === 'string' && id.length > 0;
}

/**
 * Detect the type of a raw QR code string from its prefix.
 * Handles plain-text formats (THP://BIN:, THP://SHELF:) and base64-encoded payloads.
 * Used by PlacementOverlay and other scan handlers for quick type detection.
 */
export type QRCodeType = 'bin' | 'bench' | 'shelf' | 'hole' | 'garment' | 'unknown';

export function detectQRType(qrCode: string): QRCodeType {
  if (!qrCode || !qrCode.startsWith('THP://')) return 'unknown';

  // Plain-text bin QR: THP://BIN:<label>
  if (qrCode.startsWith('THP://BIN:')) return 'bin';

  // BLU-197: plain-text bench QR: THP://BENCH:<label>
  if (qrCode.startsWith('THP://BENCH:')) return 'bench';

  // Plain-text shelf QR: THP://SHELF:<name>
  if (qrCode.startsWith('THP://SHELF:')) return 'shelf';

  // BLU-233 / BLU-685: plain-text film-storage slot QR: THP://FILMPH:<label>
  // (+ legacy THP://HOLE: for already-printed stickers). DTF film storage.
  if (qrCode.startsWith('THP://FILMPH:') || qrCode.startsWith('THP://HOLE:')) return 'hole';

  // Base64-encoded payload — try to decode and check type
  const payload = decodeThpQR(qrCode);
  if (!payload) return 'unknown';

  // Route on the presence of the stable key (`b` = bin, `g` = garment), NOT the
  // schema version. Mirrors the backend parsers (catcher/packing/print-station
  // parseQRPayload, PR #937). A label-version bump (v1→v2→v3…) must NEVER make a
  // garment scan as "unknown" — that desync is exactly what broke BLU-233 prod
  // scanning. Bins carry `b` and garments carry `g` (mutually exclusive), so the
  // bin check stays first. See memory feedback_format_change_verify_all_decoders.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vp = payload as any;
  if (typeof vp.b === 'string') return 'bin';
  if (typeof vp.g === 'string') return 'garment';

  if (isBinQR(payload)) return 'bin';
  if (isShelfQR(payload)) return 'shelf';
  if (isGarmentQR(payload)) return 'garment';

  return 'unknown';
}

/**
 * Extract the bin label from a plain-text bin QR code (THP://BIN:<label>).
 * Returns null if not a plain-text bin QR.
 */
export function extractBinLabel(qrCode: string): string | null {
  if (!qrCode.startsWith('THP://BIN:')) return null;
  return qrCode.slice(10); // Remove 'THP://BIN:'
}

/**
 * BLU-197: Extract the bench label from a plain-text bench QR (THP://BENCH:<label>).
 * Returns null if not a plain-text bench QR. Mirrors {@link extractBinLabel}.
 */
export function extractBenchLabel(qrCode: string): string | null {
  if (!qrCode.startsWith('THP://BENCH:')) return null;
  return qrCode.slice(12); // Remove 'THP://BENCH:'
}

/**
 * BLU-233 / BLU-685: Extract the film-storage slot label from a plain-text slot
 * QR — `THP://FILMPH:<label>` (film pigeon-hole), or the legacy `THP://HOLE:`
 * prefix for stickers printed before the rename. Returns null if neither
 * prefix matches. Mirrors {@link extractBinLabel} — the DTF film-storage
 * inspect surface scans this to open a slot → order → film(s) view.
 */
export function extractFilmSlotLabel(qrCode: string): string | null {
  if (qrCode.startsWith('THP://FILMPH:')) return qrCode.slice(13); // Remove 'THP://FILMPH:'
  if (qrCode.startsWith('THP://HOLE:')) return qrCode.slice(11); // legacy sticker — 'THP://HOLE:'
  return null;
}

/**
 * BLU-233: Extract the film-unit id from a PRE-PRINTED DTF film QR
 * (THP://FILM:<uuid>). Returns null if not a film QR, or if the id isn't a
 * loosely valid UUID shape. Mirrors {@link extractFilmSlotLabel}'s conventions.
 *
 * A pre-printed (speculative) film has NO garment, so its baked label encodes
 * the `prod_film_units` row id — never a garment id. The press station scans
 * this to confirm the film↔garment match against the claimed film's DB id
 * (bound at claim time) instead of the garment id path.
 */
export function extractFilmId(qrCode: string): string | null {
  if (!qrCode.startsWith('THP://FILM:')) return null;
  const id = qrCode.slice(11); // Remove 'THP://FILM:'
  // Loose UUID validation (8-4-4-4-12 hex) so a malformed tail can't pass as an id.
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    ? id
    : null;
}

/**
 * Decode and extract garment unit ID from QR code.
 * Handles V1/V2 short-key format ({ v: 1|2, g: "..." }) and legacy format
 * ({ garment_unit_id: "..." }).
 *
 * @param qrCode The scanned QR code string
 * @returns Garment unit ID or null if invalid
 */
export function extractGarmentId(qrCode: string): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload = decodeThpQR<any>(qrCode);
  if (!payload) return null;

  // Short-key format: { g: "<garment_unit_id>" }. Route on the presence of `g`,
  // NOT the schema version — a label-version bump (v1→v2→v3…) must never stop a
  // garment scanning. Mirrors the backend parsers (PR #937). See memory
  // feedback_format_change_verify_all_decoders.
  if (typeof payload.g === 'string' && payload.g.length > 0) {
    return payload.g;
  }

  // Legacy format: { garment_unit_id: "<id>" }
  if (typeof payload.garment_unit_id === 'string' && payload.garment_unit_id.length > 0) {
    return payload.garment_unit_id;
  }

  return null;
}

/**
 * Encode a garment-unit id into a legacy `THP://` garment QR string
 * (`{ garment_unit_id }`). This is the shape the print/catcher garment lookups
 * accept for programmatic "load this garment" affordances — Recently-Printed
 * rows, the resolved-issues retry feed, and cross-tech redirects — where we have
 * an id and want to route it through the same code path as a physical scan.
 *
 * Distinct from `encodeGarmentQR` in `qr-encoder.ts`, which emits the *versioned
 * short-key* payload (`{ v, g, o, s, m, t }`) printed onto real QR labels.
 * `extractGarmentId` above decodes both this legacy shape and the short-key one.
 */
export function encodeGarmentQr(garmentUnitId: string): string {
  return `THP://${btoa(JSON.stringify({ garment_unit_id: garmentUnitId }))}`;
}
